/**
 * Affiliate Marketplace Page
 * Browse public affiliate products and request access
 * For Creators to discover products they can promote
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Chip,
  Pagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';
import { useAuth } from '../../components/AuthProvider';
import {
  getPublicAffiliateProducts,
  hasRequestedProduct,
  PublicProduct,
  MarketplaceFilters,
} from '../../services/affiliateMarketplace';
import {
  addToWishlist,
  removeFromWishlist,
  isInWishlist,
  createProductRequest,
} from '../../services/affiliateRequests';
import ProductCard from '../../components/affiliate/ProductCard';

export default function AffiliateMarketplace() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [creatorId, setCreatorId] = useState<string | null>(null);
  const [products, setProducts] = useState<PublicProduct[]>([]);
  const [searchInput, setSearchInput] = useState(''); // 검색창 입력
  const [searchTexts, setSearchTexts] = useState<string[]>([]); // 일반 검색어 (회색 칩)
  const [keywords, setKeywords] = useState<string[]>([]); // @ 키워드 (검은색 칩)
  const [filters, setFilters] = useState<MarketplaceFilters>({
    sortBy: 'newest',
    commissionType: 'all',
  });
  const [wishlistMap, setWishlistMap] = useState<Map<number, string>>(new Map());
  const [requestedMap, setRequestedMap] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [suggestedKeywords, setSuggestedKeywords] = useState<string[]>([]); // 제안 키워드

  // 페이지네이션 상태
  const [page, setPage] = useState(1);
  const [itemsPerPage] = useState(30); // 한 페이지당 보여줄 아이템 수

  useEffect(() => {
    loadCreatorProfile();
    loadKeywordSuggestions();
  }, [user]);

  useEffect(() => {
    if (creatorId) {
      loadProducts();
    }
  }, [creatorId, filters, keywords, searchTexts]);

  // 검색창 입력 변경 시 키워드 제안 업데이트
  useEffect(() => {
    if (searchInput.startsWith('@')) {
      // @ 뒤의 텍스트 추출
      const searchTerm = searchInput.substring(1);
      loadKeywordSuggestions(searchTerm);
    } else if (!searchInput) {
      // 검색창이 비어있으면 랜덤 키워드 표시
      loadKeywordSuggestions();
    } else {
      // 일반 검색어면 제안 숨김
      setSuggestedKeywords([]);
    }
  }, [searchInput]);

  const loadCreatorProfile = async () => {
    if (!user) {
      setError('로그인이 필요합니다.');
      setLoading(false);
      return;
    }

    try {

      const { data: sellerProfile, error: sellerError } = await supabase
        .from('seller_profile')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (sellerError || !sellerProfile) {
        setError('크리에이터 프로필을 찾을 수 없습니다.');
        setLoading(false);
        return;
      }

      setCreatorId(sellerProfile.id);
    } catch (err: any) {
      console.error('Load creator profile error:', err);
      setError('프로필을 불러올 수 없습니다: ' + err.message);
      setLoading(false);
    }
  };

  const loadKeywordSuggestions = async (searchTerm?: string) => {
    try {
      let query = supabase.from('keywords').select('name');

      if (searchTerm && searchTerm.trim()) {
        // 검색어가 있으면 ilike로 필터링
        query = query.ilike('name', `%${searchTerm.trim()}%`).limit(10);
      } else {
        // 검색어가 없으면 랜덤으로 10개
        query = query.limit(50);
      }

      const { data, error } = await query;

      if (error) throw error;

      if (data && data.length > 0) {
        if (searchTerm && searchTerm.trim()) {
          // 검색어가 있으면 그대로 사용
          setSuggestedKeywords(data.map(k => k.name));
        } else {
          // 검색어가 없으면 랜덤 셔플 후 10개 선택
          const shuffled = [...data].sort(() => Math.random() - 0.5);
          const randomKeywords = shuffled.slice(0, 10).map(k => k.name);
          setSuggestedKeywords(randomKeywords);
        }
      } else {
        setSuggestedKeywords([]);
      }
    } catch (err: any) {
      console.error('Load keyword suggestions error:', err);
      // 실패해도 에러 표시 안함 (선택적 기능)
    }
  };

  const loadProducts = async () => {
    try {
      setLoading(true);

      // 일반 검색어를 하나로 합침
      const combinedSearchQuery = searchTexts.join(' ');

      const { data, error: productsError} = await getPublicAffiliateProducts(
        {
          ...filters,
          searchQuery: combinedSearchQuery || undefined,
          keywords: keywords.length > 0 ? keywords : undefined
        },
        200, // 더 많은 상품 로드
        0
      );

      if (productsError) throw productsError;

      setProducts(data || []);

      // Load wishlist and request status for each product
      if (creatorId && data) {
        const wishlistChecks = await Promise.all(
          data.map((p) => isInWishlist(creatorId, p.product_id))
        );
        const requestChecks = await Promise.all(
          data.map((p) => hasRequestedProduct(creatorId, p.product_id))
        );

        const newWishlistMap = new Map<number, string>();
        const newRequestedSet = new Set<number>();

        data.forEach((product, index) => {
          if (wishlistChecks[index].isInWishlist && wishlistChecks[index].wishlistItemId) {
            newWishlistMap.set(product.product_id, wishlistChecks[index].wishlistItemId!);
          }
          if (requestChecks[index].hasRequested) {
            newRequestedSet.add(product.product_id);
          }
        });

        setWishlistMap(newWishlistMap);
        setRequestedMap(newRequestedSet);
      }
    } catch (err: any) {
      setError('상품을 불러올 수 없습니다: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    const value = searchInput.trim();
    if (!value) return;

    if (value.startsWith('@')) {
      // @ 키워드
      const keyword = value.substring(1).trim();
      if (keyword && keywords.length < 3 && !keywords.includes(keyword)) {
        setKeywords([...keywords, keyword]);
        setSearchInput('');
      } else if (keywords.length >= 3) {
        setError('키워드는 최대 3개까지 입력 가능합니다');
        setTimeout(() => setError(null), 3000);
      }
    } else {
      // 일반 검색어
      if (!searchTexts.includes(value)) {
        setSearchTexts([...searchTexts, value]);
        setSearchInput('');
      }
    }
  };

  const handleRemoveKeyword = (index: number) => {
    const newKeywords = [...keywords];
    newKeywords.splice(index, 1);
    setKeywords(newKeywords);
  };

  const handleRemoveSearchText = (index: number) => {
    const newSearchTexts = [...searchTexts];
    newSearchTexts.splice(index, 1);
    setSearchTexts(newSearchTexts);
  };

  const handleSearch = () => {
    loadProducts();
  };

  const handleSuggestedKeywordClick = (keyword: string) => {
    // 이미 추가된 키워드인지 확인
    if (keywords.includes(keyword)) {
      return;
    }

    // 최대 3개 제한
    if (keywords.length >= 3) {
      setError('키워드는 최대 3개까지 입력 가능합니다');
      setTimeout(() => setError(null), 3000);
      return;
    }

    // 키워드 추가
    setKeywords([...keywords, keyword]);
  };

  const handleAddToWishlist = async (productId: number) => {
    if (!creatorId) return;

    try {
      const { error } = await addToWishlist(creatorId, productId);
      if (error) throw error;

      setSuccess('위시리스트에 추가되었습니다.');
      loadProducts(); // Reload to update wishlist status
    } catch (err: any) {
      setError('위시리스트 추가 실패: ' + err.message);
    }
  };

  const handleRemoveFromWishlist = async (productId: number) => {
    const wishlistItemId = wishlistMap.get(productId);
    if (!wishlistItemId) return;

    try {
      const { error } = await removeFromWishlist(wishlistItemId);
      if (error) throw error;

      setSuccess('위시리스트에서 제거되었습니다.');
      loadProducts(); // Reload to update wishlist status
    } catch (err: any) {
      setError('위시리스트 제거 실패: ' + err.message);
    }
  };

  const handleRequestAccess = async (productId: number) => {
    if (!creatorId) return;

    try {
      const { error } = await createProductRequest(creatorId, productId);
      if (error) throw error;

      setSuccess('접근 요청을 보냈습니다!');
      loadProducts(); // Reload to update request status
    } catch (err: any) {
      setError('접근 요청 실패: ' + err.message);
    }
  };

  const handleProductClick = (productId: number) => {
    // Open Flutter web product page in new tab
    // Production URL: https://clozet.my
    const flutterWebUrl = `https://clozet.my/#/product/${productId}`;
    window.open(flutterWebUrl, '_blank', 'noopener,noreferrer');
  };

  // 페이지네이션 계산
  const totalPages = Math.ceil(products.length / itemsPerPage);
  const paginatedProducts = products.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' }); // 페이지 변경시 상단으로 스크롤
  };

  // 필터나 검색어 변경 시 페이지 초기화
  useEffect(() => {
    setPage(1);
  }, [filters, keywords, searchTexts]);

  return (
    <Box sx={styles.pageContainer}>
      <Typography variant="h4" fontWeight="bold" sx={{ ...styles.title, mb: 3 }}>
        어필리에이트 마켓플레이스
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Search and Filters */}
      <Paper sx={{ ...styles.paper, p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="flex-start">
          {/* Row 1: Search, Sort, Commission Type */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="@키워드로 추가 또는 제품명으로 검색"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearchSubmit();
                }
              }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: isDarkMode ? '#aaa' : '#666' }} />,
              }}
              sx={styles.input}
            />
            {/* 키워드 및 검색어 칩 표시 */}
            {(keywords.length > 0 || searchTexts.length > 0) && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {/* @ 키워드 (검은색 배경) */}
                {keywords.map((keyword, index) => (
                  <Chip
                    key={`keyword-${index}`}
                    label={keyword}
                    onDelete={() => handleRemoveKeyword(index)}
                    deleteIcon={<CloseIcon />}
                    sx={{
                      bgcolor: '#000',
                      color: '#fff',
                      '& .MuiChip-deleteIcon': {
                        color: '#fff',
                      },
                    }}
                  />
                ))}
                {/* 일반 검색어 (회색 배경) */}
                {searchTexts.map((text, index) => (
                  <Chip
                    key={`text-${index}`}
                    label={text}
                    onDelete={() => handleRemoveSearchText(index)}
                    deleteIcon={<CloseIcon />}
                    sx={{
                      bgcolor: isDarkMode ? '#555' : '#e0e0e0',
                      color: isDarkMode ? '#fff' : '#000',
                    }}
                  />
                ))}
              </Box>
            )}
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth sx={styles.input}>
              <InputLabel>정렬</InputLabel>
              <Select
                value={filters.sortBy || 'newest'}
                label="정렬"
                onChange={(e) => setFilters({ ...filters, sortBy: e.target.value as any })}
              >
                <MenuItem value="newest">최신순</MenuItem>
                <MenuItem value="commission">수수료 높은순</MenuItem>
                <MenuItem value="price_low">가격 낮은순</MenuItem>
                <MenuItem value="price_high">가격 높은순</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth sx={styles.input}>
              <InputLabel>수수료 타입</InputLabel>
              <Select
                value={filters.commissionType || 'all'}
                label="수수료 타입"
                onChange={(e) => setFilters({ ...filters, commissionType: e.target.value as any })}
              >
                <MenuItem value="all">전체</MenuItem>
                <MenuItem value="percentage">퍼센트</MenuItem>
                <MenuItem value="fixed">고정금액</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Row 2: Commission Filters */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="number"
              label="최소 수수료 (%)"
              placeholder="예: 10"
              value={filters.minCommissionPercentage || ''}
              onChange={(e) =>
                setFilters({ ...filters, minCommissionPercentage: e.target.value ? Number(e.target.value) : undefined })
              }
              sx={styles.input}
              inputProps={{ min: 0, max: 100 }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="number"
              label="최소 고정금액 (원)"
              placeholder="예: 5000"
              value={filters.minCommissionFixed || ''}
              onChange={(e) =>
                setFilters({ ...filters, minCommissionFixed: e.target.value ? Number(e.target.value) : undefined })
              }
              sx={styles.input}
              inputProps={{ min: 0 }}
            />
          </Grid>

          {/* Search Button */}
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              sx={{ height: 56 }}
            >
              검색
            </Button>
          </Grid>
        </Grid>

        {/* 키워드 제안 - 검색창 아래에 표시 */}
        {suggestedKeywords.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ ...styles.text, mb: 1, fontWeight: 600 }}>
              {searchInput.startsWith('@') && searchInput.length > 1 ? '검색 결과' : '추천 키워드'}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {suggestedKeywords.map((keyword, index) => (
                <Box
                  key={`suggested-${index}`}
                  onClick={() => handleSuggestedKeywordClick(keyword)}
                  sx={{
                    padding: '6px 12px',
                    bgcolor: '#000',
                    color: '#fff',
                    borderRadius: '16px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 500,
                    transition: 'transform 0.2s, opacity 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      opacity: 0.9,
                    },
                  }}
                >
                  {keyword}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Products Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : products.length === 0 ? (
        <Paper sx={{ ...styles.paper, p: 8, textAlign: 'center' }}>
          <Typography variant="h6" sx={styles.text}>
            검색 결과가 없습니다
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={styles.text}>
            다른 검색어나 필터를 시도해보세요
          </Typography>
        </Paper>
      ) : (
        <>
          {/* 검색 결과 정보 */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={styles.text}>
              총 {products.length}개 상품
            </Typography>
            {totalPages > 1 && (
              <Typography variant="body2" color="text.secondary" sx={styles.text}>
                페이지 {page} / {totalPages}
              </Typography>
            )}
          </Box>

          <Grid container spacing={3}>
            {paginatedProducts.map((product) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={product.product_id}>
                <ProductCard
                  productId={product.product_id}
                  productName={product.product_name}
                  price={product.price}
                  mainImage={product.main_images?.[0] || ''}
                  commissionRate={product.default_commission_rate}
                  commissionType={product.default_commission_type}
                  brandName={product.seller.store_name}
                  brandImage={product.seller.profile_image}
                  keywords={product.keywords}
                  isInWishlist={wishlistMap.has(product.product_id)}
                  hasRequested={requestedMap.has(product.product_id)}
                  isOwnProduct={product.seller_id === creatorId}
                  affiliateStartDate={product.affiliate_start_date}
                  affiliateEndDate={product.affiliate_end_date}
                  affiliateMaxUses={product.affiliate_max_uses}
                  affiliateCurrentUses={product.affiliate_current_uses}
                  onAddToWishlist={handleAddToWishlist}
                  onRemoveFromWishlist={handleRemoveFromWishlist}
                  onRequestAccess={handleRequestAccess}
                  onClick={handleProductClick}
                />
              </Grid>
            ))}
          </Grid>

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
